import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/layouts/PageContainer";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Users, ChevronLeft, AlertCircle, Trash2 } from "lucide-react";

function formatLastActive(dateStr) {
  if (!dateStr) return "Never";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? "s" : ""} ago`;
  if (diffHrs < 24) return `${diffHrs} hour${diffHrs !== 1 ? "s" : ""} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;

  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function TeacherActivity() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { getToken } = useAuth();

  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/teacher-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load teacher usage");
      const data = await res.json();
      setTeachers(data.teachers ?? []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const removeMember = async (userId, name) => {
    if (!confirm(`Remove ${name} from this school?`)) return;
    setActionInProgress(userId);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/members/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to remove member");
      }
      toast.success("Member removed.");
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        My School
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Users size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-normal text-foreground">Teacher Activity</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {teachers.length} teacher{teachers.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {teachers.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center rounded-2xl border border-dashed border-border bg-muted/20">
          <Users size={28} className="text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No teachers in this school yet</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Teacher</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Classes</TableHead>
                <TableHead>Reports</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {teachers.map((teacher) => {
                const uid = teacher.userId?.toString?.() ?? teacher.userId;
                const busy = actionInProgress === uid;
                return (
                  <TableRow key={uid}>
                    <TableCell className="font-medium text-foreground">
                      {teacher.name || "-"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {teacher.email || "-"}
                    </TableCell>
                    <TableCell>{teacher.classCount ?? 0}</TableCell>
                    <TableCell>{teacher.reportCount ?? 0}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatLastActive(teacher.lastActiveAt)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        title="Remove member"
                        disabled={busy}
                        onClick={() => removeMember(uid, teacher.name)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </PageContainer>
  );
}
